// 效果模块导出
export * from './shadow';
export { SKEW_PRESETS, SKEW_RANGE, getNodeMatrix, MATRIX_UTILS, MatrixSerializer } from './matrix';

// 效果函数导入
import { drawBackdropFilter } from './backdrop';
import { drawBlendMode } from './blend';
import { drawClipPath } from './clip';
import { drawFilter } from './filter';
import { drawMatrix } from './matrix';
import { drawNoise } from './noise';
import { drawOpacity } from './opacity';
import { drawShadow } from './shadow';

// 重新导出效果函数
export {
  drawBackdropFilter,
  drawBlendMode,
  drawClipPath,
  drawFilter,
  drawMatrix,
  drawNoise,
  drawOpacity,
  drawShadow,
};

// ============================================
// 效果位掩码优化
// 使用位掩码快速检测需要应用的效果，避免运行时条件检查
// ============================================

// 效果位标志 (使用位运算进行快速检测)
const EFFECT_NONE = 0;
const EFFECT_NOISE = 1 << 0; // 1
const EFFECT_MATRIX = 1 << 1; // 2 (transform/rotation/flip)
const EFFECT_OPACITY = 1 << 2; // 4
const EFFECT_CLIP_PATH = 1 << 3; // 8
const EFFECT_BACKDROP_FILTER = 1 << 4; // 16
const EFFECT_FILTER = 1 << 5; // 32
const EFFECT_SHADOW = 1 << 6; // 64
const EFFECT_BLEND_MODE = 1 << 7; // 128

// 效果函数数组，索引对应位掩码的位位置
const EFFECT_FUNCTIONS = [
  drawNoise, // bit 0
  drawMatrix, // bit 1
  drawOpacity, // bit 2
  drawClipPath, // bit 3
  drawBackdropFilter, // bit 4
  drawFilter, // bit 5
  drawShadow, // bit 6
  drawBlendMode, // bit 7
];

const EFFECT_COUNT = EFFECT_FUNCTIONS.length;

// 静态化字符串常量，避免运行时字符串比较开销
const STR_NONE = 'none';
const STR_NORMAL = 'normal';
const STR_NUMBER = 'number';

// 节点类型定义
interface EffectNode {
  noise?: number;
  transform?: string;
  rotation?: number;
  flipX?: boolean;
  flipY?: boolean;
  opacity?: number;
  clipPath?: string;
  backdropFilter?: string;
  filter?: string;
  shadow?: string;
  blendMode?: string;
}

// 渲染上下文类型定义
interface RenderContext {
  context: CanvasRenderingContext2D;
  providers: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * 计算节点的效果位掩码
 * V8 优化：使用位运算，避免多次属性访问
 * @param node - 渲染节点
 * @returns 效果位掩码
 */
export function computeEffectMask(node: EffectNode): number {
  let mask = EFFECT_NONE;

  // Noise 效果
  if (node.noise && node.noise !== 0) {
    mask |= EFFECT_NOISE;
  }

  // Matrix 效果 (transform/rotation/flip)
  if (
    (node.transform && node.transform !== STR_NONE) ||
    (typeof node.rotation === STR_NUMBER && node.rotation !== 0) ||
    node.flipX === true ||
    node.flipY === true
  ) {
    mask |= EFFECT_MATRIX;
  }

  // Opacity 效果
  if (typeof node.opacity === STR_NUMBER && node.opacity! >= 0 && node.opacity! < 1) {
    mask |= EFFECT_OPACITY;
  }

  // ClipPath 效果
  if (node.clipPath && node.clipPath !== STR_NONE) {
    mask |= EFFECT_CLIP_PATH;
  }

  // BackdropFilter 效果
  if (node.backdropFilter && node.backdropFilter !== STR_NONE) {
    mask |= EFFECT_BACKDROP_FILTER;
  }

  // Filter 效果
  if (node.filter && node.filter !== STR_NONE) {
    mask |= EFFECT_FILTER;
  }

  // Shadow 效果
  if (node.shadow && node.shadow !== STR_NONE) {
    mask |= EFFECT_SHADOW;
  }

  // BlendMode 效果
  if (node.blendMode && node.blendMode !== STR_NORMAL) {
    mask |= EFFECT_BLEND_MODE;
  }

  return mask;
}

/**
 * 应用效果链
 * 使用位掩码跳过不需要的效果，减少函数调用开销
 * @param renderContext - 渲染上下文
 * @param node - 渲染节点
 * @param renderFn - 渲染函数
 */
export function applyEffects(
  renderContext: RenderContext,
  node: EffectNode,
  renderFn: () => void
): void {
  // 计算效果位掩码
  const effectMask = computeEffectMask(node);

  // 快速路径：没有任何效果，直接渲染
  if (effectMask === EFFECT_NONE) {
    renderFn();
    return;
  }

  // 使用位掩码优化的递归应用
  // 只处理实际需要的效果
  function applyEffect(bitIndex: number): void {
    // 跳过到下一个需要的效果
    while (bitIndex < EFFECT_COUNT && (effectMask & (1 << bitIndex)) === 0) {
      bitIndex++;
    }

    // 所有效果已处理完毕，执行渲染
    if (bitIndex >= EFFECT_COUNT) {
      renderFn();
      return;
    }

    // 应用当前效果，然后继续下一个
    EFFECT_FUNCTIONS[bitIndex](renderContext, node, () => applyEffect(bitIndex + 1));
  }

  applyEffect(0);
}

// 导出效果掩码常量（供外部使用，如调试）
export const EFFECT_MASKS = {
  NONE: EFFECT_NONE,
  NOISE: EFFECT_NOISE,
  MATRIX: EFFECT_MATRIX,
  OPACITY: EFFECT_OPACITY,
  CLIP_PATH: EFFECT_CLIP_PATH,
  BACKDROP_FILTER: EFFECT_BACKDROP_FILTER,
  FILTER: EFFECT_FILTER,
  SHADOW: EFFECT_SHADOW,
  BLEND_MODE: EFFECT_BLEND_MODE,
} as const;
